/**
 * Deterministic per-class colour palette for Wochenplaner.
 *
 * Classes have no stored colour, so each class is assigned a stable colour
 * derived from its id (falling back to its name), picked from a Catppuccin-based
 * palette. The `tint` background is derived from the picked colour via
 * `color-mix()` so the badge adapts (roughly) between light and dark mode — the
 * same technique `getSubjectStyle()` uses for custom subject colours.
 */
const CLASS_PALETTE = [
  '#1e66f5', // blue
  '#8839ef', // mauve
  '#40a02b', // green
  '#fe640b', // peach
  '#179299', // teal
  '#ea76cb', // pink
  '#df8e1d', // yellow
  '#7287fd', // lavender
  '#209fb5', // sapphire
  '#d20f39', // red
  '#04a5e5', // sky
  '#e64553', // maroon
];

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

/** Stable colour for an arbitrary key (class id or name). */
export function getClassColor(key) {
  return CLASS_PALETTE[hashString(String(key || '')) % CLASS_PALETTE.length];
}

/**
 * Returns `{ name, ink, tint }` for a class id, or `null` when the class is not
 * found. `ink` is the strong text/dot colour, `tint` a soft background.
 *
 * @param {string} classId
 * @param {Array<{id: string, name: string}>} [classes]
 */
export function getClassStyle(classId, classes) {
  const cls = classes?.find(c => c.id === classId);
  if (!cls) return null;
  const ink = getClassColor(cls.id || cls.name);
  return {
    name: cls.name,
    ink,
    tint: `color-mix(in srgb, ${ink} 20%, var(--surface))`,
  };
}
